/**
 * Pharmacokinetic engine — one-compartment model with first-order absorption.
 *
 * Bateman equation:
 *   fraction_remaining(t) = (ka / (ka - ke)) * (e^(-ke·t) - e^(-ka·t))
 *
 * For IM oil esters and SC GLP-1s, absorption is the rate-limiting step
 * (flip-flop kinetics) so ka < ke. The formula still applies.
 *
 * Values are "estimated active amount", not measured serum concentration.
 */

import { positive, validDate } from "./validation";

export type CompoundParams = {
  absorptionHalfLifeHours: number;
  eliminationHalfLifeHours: number;
  bioavailability: number;
};

export type Dose = {
  takenAt: Date;
  amount: number;
};

const HOUR_MS = 60 * 60 * 1000;

export function hoursBetween(later: Date, earlier: Date): number {
  validDate(later);
  validDate(earlier);
  return (later.getTime() - earlier.getTime()) / HOUR_MS;
}

export function rateConstants(p: CompoundParams): { ka: number; ke: number } {
  positive(p.absorptionHalfLifeHours, "Absorption half-life");
  positive(p.eliminationHalfLifeHours, "Elimination half-life");
  positive(p.bioavailability, "Bioavailability");
  if (p.bioavailability > 1) throw new Error("Bioavailability cannot exceed 1");
  return {
    ka: Math.LN2 / p.absorptionHalfLifeHours,
    ke: Math.LN2 / p.eliminationHalfLifeHours,
  };
}

/**
 * Fraction of dose still active at time t hours after injection.
 * Returns 0 for t <= 0.
 */
export function batemanFraction(tHours: number, p: CompoundParams): number {
  const { ka, ke } = rateConstants(p);
  if (!Number.isFinite(tHours)) throw new Error("Elapsed hours must be finite");
  if (tHours <= 0) return 0;
  const diff = ka - ke;

  if (diff === 0) {
    // ka == ke degenerate case (limit)
    return ka * tHours * Math.exp(-ka * tHours);
  }

  return (
    (ka / Math.abs(diff)) *
    Math.exp(-Math.min(ka, ke) * tHours) *
    -Math.expm1(-Math.abs(diff) * tHours)
  );
}

/**
 * Active amount contributed by a single dose at evaluation time.
 */
export function doseContributionAt(
  evalAt: Date,
  dose: Dose,
  p: CompoundParams,
): number {
  const t = hoursBetween(evalAt, dose.takenAt);
  positive(dose.amount, "Dose");
  rateConstants(p);
  if (t <= 0) return 0;
  return dose.amount * p.bioavailability * batemanFraction(t, p);
}

/**
 * Sum of active amounts across all past doses at evaluation time.
 */
export function activeAmountAt(
  evalAt: Date,
  doses: readonly Dose[],
  p: CompoundParams,
): number {
  validDate(evalAt);
  rateConstants(p);
  let total = 0;
  for (const d of doses) {
    total += doseContributionAt(evalAt, d, p);
  }
  if (!Number.isFinite(total)) throw new Error("Active amount is not finite");
  return total;
}

/**
 * Sample the active-amount curve across a time window.
 */
export function curveSamples(
  fromTime: Date,
  toTime: Date,
  doses: readonly Dose[],
  p: CompoundParams,
  stepMinutes = 60,
): Array<{ t: Date; mg: number }> {
  validDate(fromTime);
  validDate(toTime);
  positive(stepMinutes, "Sample interval");
  const stepMs = stepMinutes * 60 * 1000;
  if (+toTime < +fromTime || (+toTime - +fromTime) / stepMs > 100_000)
    throw new Error("Invalid or excessive sample window");
  const out: Array<{ t: Date; mg: number }> = [];
  for (let t = fromTime.getTime(); t <= toTime.getTime(); t += stepMs) {
    const d = new Date(t);
    out.push({ t: d, mg: activeAmountAt(d, doses, p) });
  }
  return out;
}
